<div class="auth-card">
  <!-- Logo -->
  <a routerLink="/" class="auth-logo">
    <img src="assets/images/NBHA-Norway.png" alt="NBHA Norway" />
  </a>

  @if (registered) {
    <!-- Success State -->
    <div class="auth-header">
      <h1>Check your email</h1>
      <p>We've sent you a verification link</p>
    </div>

    <div class="alert alert-success">
      <i class="fa fa-check-circle"></i>
      <div>
        <strong>Registration successful!</strong>
        <p class="mb-0">Please check your email and click the verification link to activate your account.</p>
      </div>
    </div>

    <button
      type="button"
      class="btn btn-outline"
      routerLink="/auth/login"
    >
      <i class="fa fa-arrow-left me-2"></i>
      Back to login
    </button>
  } @else {
    <!-- Registration Form -->
    <div class="auth-header">
      <h1>Create your account</h1>
      <p>Join NBHA Norway today</p>
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">
      <!-- Name Fields (2 columns) -->
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label for="first_name" class="form-label">First name</label>
            <input
              type="text"
              id="first_name"
              formControlName="first_name"
              class="form-control"
              [class.is-invalid]="form.get('first_name')?.invalid && form.get('first_name')?.touched"
              placeholder="John"
              required
            />
            <span *ngIf="form.get('first_name')?.invalid && form.get('first_name')?.touched" class="invalid-feedback">
              First name is required
            </span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label for="last_name" class="form-label">Last name</label>
            <input
              type="text"
              id="last_name"
              formControlName="last_name"
              class="form-control"
              [class.is-invalid]="form.get('last_name')?.invalid && form.get('last_name')?.touched"
              placeholder="Doe"
              required
            />
            <span *ngIf="form.get('last_name')?.invalid && form.get('last_name')?.touched" class="invalid-feedback">
              Last name is required
            </span>
          </div>
        </div>
      </div>

      <!-- Email Field -->
      <div class="form-group">
        <label for="email" class="form-label">Email address</label>
        <input
          type="email"
          id="email"
          formControlName="email"
          class="form-control"
          [class.is-invalid]="form.get('email')?.invalid && form.get('email')?.touched"
          placeholder="you@example.com"
          required
        />
        <span *ngIf="form.get('email')?.invalid && form.get('email')?.touched" class="invalid-feedback">
          Please enter a valid email address
        </span>
      </div>

      <!-- Phone Number Field -->
      <div class="form-group">
        <label for="phone_number" class="form-label">Phone number</label>
        <div class="phone-input-group">
          <select
            formControlName="phone_prefix"
            class="phone-prefix-select"
          >
            <option value="+47">🇳🇴 +47</option>
            <option value="+1">🇺🇸 +1</option>
            <option value="+44">🇬🇧 +44</option>
            <option value="+46">🇸🇪 +46</option>
            <option value="+45">🇩🇰 +45</option>
            <option value="+358">🇫🇮 +358</option>
            <option value="+49">🇩🇪 +49</option>
            <option value="+33">🇫🇷 +33</option>
            <option value="+34">🇪🇸 +34</option>
            <option value="+39">🇮🇹 +39</option>
            <option value="+31">🇳🇱 +31</option>
          </select>
          <input
            type="tel"
            id="phone_number"
            formControlName="phone_number"
            class="form-control phone-number-input"
            [class.is-invalid]="form.get('phone_number')?.invalid && form.get('phone_number')?.touched"
            placeholder="123 45 678"
            required
          />
        </div>
        <span *ngIf="form.get('phone_number')?.invalid && form.get('phone_number')?.touched" class="invalid-feedback">
          Please enter a valid phone number
        </span>
      </div>

      <!-- Password Field -->
      <div class="form-group">
        <label for="password" class="form-label">Password</label>
        <div class="password-toggle">
          <input
            [type]="passwordVisible ? 'text' : 'password'"
            id="password"
            formControlName="password"
            class="form-control"
            [class.is-invalid]="form.get('password')?.hasError('passwordStrength') && form.get('password')?.touched"
            placeholder="Create a strong password"
            required
          />
          <i
            [class]="passwordVisible ? 'fa fa-eye-slash' : 'fa fa-eye'"
            class="password-toggle-icon"
            (click)="togglePasswordVisibility('password')"
          ></i>
        </div>
        <span *ngIf="form.get('password')?.hasError('passwordStrength') && form.get('password')?.touched" class="invalid-feedback">
          Password must be at least 8 characters with 1 number and 1 special character
        </span>
      </div>

      <!-- Confirm Password Field -->
      <div class="form-group">
        <label for="confirmPassword" class="form-label">Confirm password</label>
        <div class="password-toggle">
          <input
            [type]="confirmPasswordVisible ? 'text' : 'password'"
            id="confirmPassword"
            formControlName="confirmPassword"
            class="form-control"
            [class.is-invalid]="form.hasError('passwordMismatch') && form.get('confirmPassword')?.touched"
            placeholder="Re-enter your password"
            required
          />
          <i
            [class]="confirmPasswordVisible ? 'fa fa-eye-slash' : 'fa fa-eye'"
            class="password-toggle-icon"
            (click)="togglePasswordVisibility('confirmPassword')"
          ></i>
        </div>
        <span *ngIf="form.hasError('passwordMismatch') && form.get('confirmPassword')?.touched" class="invalid-feedback">
          Passwords do not match
        </span>
      </div>

      <!-- Password Requirements -->
      <div class="password-requirements">
        <div class="password-requirements-title">Password must include:</div>
        <div class="password-requirement">
          <i class="fa fa-check-circle"></i>
          <span>At least 8 characters</span>
        </div>
        <div class="password-requirement">
          <i class="fa fa-check-circle"></i>
          <span>At least 1 number</span>
        </div>
        <div class="password-requirement">
          <i class="fa fa-check-circle"></i>
          <span>At least 1 special character</span>
        </div>
      </div>

      <!-- Register Button -->
      <div class="form-group mt-4">
        <button
          type="submit"
          class="btn btn-primary"
          [disabled]="form.invalid || isSubmitting"
        >
          <span *ngIf="!isSubmitting">Create account</span>
          <span *ngIf="isSubmitting">
            <i class="fa fa-spinner spinner me-2"></i>
            Creating account...
          </span>
        </button>
      </div>
    </form>

    <!-- Divider -->
    <div class="auth-divider">
      <span>or</span>
    </div>

    <!-- Login Link -->
    <div class="auth-link">
      Already have an account? <a routerLink="/auth/login">Sign in</a>
    </div>
  }
</div>
